import { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { AppLayout } from '@/components/layout/AppLayout';
import { WelcomeHeader } from '@/components/dashboard/WelcomeHeader';
import { StatsCards } from '@/components/dashboard/StatsCards';
import { RecentActivity } from '@/components/dashboard/RecentActivity';
import { TodayStats } from '@/components/dashboard/TodayStats';
import { InfoSidebar } from '@/components/dashboard/InfoSidebar';
import { ConfigurationProgress } from '@/components/dashboard/ConfigurationProgress';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { NoWorkspaceAccess } from '@/components/auth/NoWorkspaceAccess';

const TicketsChart = lazy(() =>
  import('@/components/dashboard/TicketsChart').then((m) => ({ default: m.TicketsChart }))
);

function TicketsChartFallback() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-5 w-40" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-[200px] w-full" />
      </CardContent>
    </Card>
  );
}

type OnboardingStatus = 'checking' | 'completed' | 'needed' | 'no_access' | 'error';

interface PendingInvitationDetails {
  email: string;
  workspaceName: string;
  expiresAt: string | null;
}

function Dashboard() {
  usePageTitle('Tableau de bord');

  return (
    <WorkspaceProvider>
      <AppLayout>
        <div className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">
            <div className="flex gap-6">
              {/* Main content */}
              <div className="flex-1 space-y-6">
                {/* Welcome Header */}
                <WelcomeHeader />

                {/* Configuration Progress */}
                <ConfigurationProgress />

                {/* Today's Stats */}
                <TodayStats />

                {/* Stats */}
                <StatsCards />

                {/* Tickets Chart */}
                <Suspense fallback={<TicketsChartFallback />}>
                  <TicketsChart />
                </Suspense>

                {/* Recent Activity */}
                <RecentActivity />

                {/* InfoSidebar compacte pour tablettes (visible lg, cachée xl) */}
                <div className="block lg:block xl:hidden">
                  <InfoSidebar />
                </div>
              </div>

              {/* Info sidebar - now inside the scrollable container */}
              <div className="hidden xl:block w-80 shrink-0">
                <div className="sticky top-0 space-y-6">
                  <InfoSidebar />
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    </WorkspaceProvider>
  );
}

const Index = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<OnboardingStatus>('checking');
  const [pendingInvitation, setPendingInvitation] = useState<PendingInvitationDetails | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const checkInProgress = useRef(false);

  const runCheck = async () => {
    if (!user) return;
    checkInProgress.current = true;

    try {
      // Helper: read profile + membership in parallel
      const fetchState = async () => {
        const [{ data: profile, error: profileError }, { data: membership, error: membershipError }] = await Promise.all([
          supabase
            .from('profiles')
            .select('onboarding_completed_at')
            .eq('id', user.id)
            .maybeSingle(),
          supabase
            .from('workspace_members')
            .select('workspace_id')
            .eq('user_id', user.id)
            .limit(1)
            .maybeSingle(),
        ]);
        if (profileError) logger.warn('Error fetching profile', profileError, 'Index');
        if (membershipError) logger.warn('Error fetching workspace membership', membershipError, 'Index');
        return { profile, membership };
      };

      let { profile, membership } = await fetchState();

      // Guardrail: if onboarding flag is set but membership is missing, retry briefly
      // (covers replication / cache lag right after the atomic RPC commit).
      if (profile?.onboarding_completed_at && !membership) {
        for (let attempt = 0; attempt < 4 && !membership; attempt++) {
          await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
          const next = await fetchState();
          membership = next.membership;
          profile = next.profile;
        }
      }

      if (!membership) {
        // Look up pending invitations for this email — surfaces the
        // "access awaiting validation" state instead of forcing onboarding.
        const { data: invitations } = await supabase
          .from('pending_invitations')
          .select('email, expires_at, workspaces:workspace_id(name)')
          .is('accepted_at', null)
          .gt('expires_at', new Date().toISOString())
          .order('created_at', { ascending: false })
          .limit(1);

        const invitation = invitations?.[0];
        if (invitation) {
          setPendingInvitation({
            email: invitation.email,
            workspaceName:
              (invitation.workspaces as { name?: string } | null)?.name || 'votre espace',
            expiresAt: invitation.expires_at,
          });
          sessionStorage.removeItem('onboarding_redirect_attempts');
          setStatus('no_access');
          return;
        }

        // Loop guardrail: if we just came from /onboarding and STILL have no
        // membership, surface no_access state instead of an infinite redirect.
        const cameFromOnboarding =
          typeof window !== 'undefined' &&
          (sessionStorage.getItem('onboarding_redirect_attempts') || '0');
        const attempts = parseInt(cameFromOnboarding as string, 10) || 0;

        if (profile?.onboarding_completed_at || attempts >= 2) {
          if (attempts >= 2) {
            logger.error('Onboarding redirect loop detected', { userId: user.id, attempts }, 'Index');
          }
          sessionStorage.removeItem('onboarding_redirect_attempts');
          setPendingInvitation(null);
          setStatus('no_access');
          return;
        }

        sessionStorage.setItem('onboarding_redirect_attempts', String(attempts + 1));
        setStatus('needed');
        return;
      }

      // Membership found -> clear loop counter
      sessionStorage.removeItem('onboarding_redirect_attempts');
      setPendingInvitation(null);

      if (profile?.onboarding_completed_at) {
        setStatus('completed');
      } else {
        setStatus('needed');
      }
    } catch (error) {
      logger.error('Error checking onboarding status:', error, 'Index');
      setStatus('error');
    } finally {
      checkInProgress.current = false;
    }
  };

  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate('/auth', { replace: true });
      return;
    }

    if (status !== 'checking' || checkInProgress.current) return;

    runCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, status, navigate]);

  // Navigate to onboarding only after status is determined
  useEffect(() => {
    if (status === 'needed') {
      navigate('/onboarding', { replace: true });
    }
  }, [status, navigate]);

  const handleRefreshAccess = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setStatus('checking');
    await runCheck();
    setRefreshing(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/auth', { replace: true });
  };

  // Redirect in progress - render nothing to avoid flash
  if (status === 'needed') {
    return null;
  }

  // Show Dashboard during auth/onboarding check - it has its own skeletons
  if (loading || status === 'checking') {
    return <Dashboard />;
  }

  // No user means we're redirecting to auth
  if (!user) {
    return null;
  }

  // Awaiting workspace access — dedicated screen with validation procedure
  if (status === 'no_access') {
    return (
      <NoWorkspaceAccess
        userEmail={user.email}
        pendingInvitation={pendingInvitation}
        onRefresh={handleRefreshAccess}
        onSignOut={handleSignOut}
        refreshing={refreshing}
      />
    );
  }

  // Render dashboard for completed or error status
  return <Dashboard />;
};

export default Index;
